import { FieldGrid } from './FieldGrid';

// Physical constants
const C = 299792458;         // speed of light (m/s)
const EPSILON_0 = 8.854e-12; // vacuum permittivity
const MU_0 = 1.257e-6;      // vacuum permeability

/**
 * 2D FDTD solver for TE mode (E_x, E_y, H_z).
 *
 * Maxwell's equations in 2D TE mode:
 *   ∂E_x/∂t = (1/ε₀)(∂H_z/∂y)
 *   ∂E_y/∂t = -(1/ε₀)(∂H_z/∂x)
 *   ∂H_z/∂t = (1/μ₀)(∂E_x/∂y - ∂E_y/∂x)
 *
 * Courant stability condition: dt < dx / (c * sqrt(2))
 */
export class FDTDSolver {
  /**
   * Calculate the maximum stable timestep for a given cell size.
   * Courant condition for 2D: dt < dx / (c * sqrt(2))
   */
  static courantTimestep(cellSizeMeters: number): number {
    return cellSizeMeters / (C * Math.SQRT2) * 0.99; // 99% of Courant limit for safety
  }

  /**
   * Perform one FDTD update step on a field grid.
   *
   * Uses the standard Yee staggered grid:
   * - E fields are sampled at integer grid points
   * - H field is sampled at half-integer grid points
   *
   * @param grid The field grid to update
   * @param dt Timestep in seconds (must satisfy Courant condition)
   */
  update(grid: FieldGrid, dt: number): void {
    const res = grid.resolution;
    const nw = grid.wavelengthCount;
    const dx = grid.cellSize;

    // Precompute update coefficients
    const dtOverEps = dt / EPSILON_0;
    const dtOverMu = dt / MU_0;
    const invDx = 1 / dx;

    // --- Update H_z (magnetic field) ---
    // H_z^(n+1/2) = H_z^(n-1/2) + (dt/μ₀) * (∂E_x/∂y - ∂E_y/∂x)
    for (let y = 0; y < res - 1; y++) {
      for (let x = 0; x < res - 1; x++) {
        // PML damping
        const sigma = grid.getPMLDamping(x, y);
        const dampFactor = 1 / (1 + sigma * dt);

        for (let w = 0; w < nw; w++) {
          const idx = grid.index(x, y, w);
          const idx_xp1 = grid.index(x + 1, y, w);
          const idx_yp1 = grid.index(x, y + 1, w);

          const dEx_dy = (grid.E_x[idx_yp1] - grid.E_x[idx]) * invDx;
          const dEy_dx = (grid.E_y[idx_xp1] - grid.E_y[idx]) * invDx;

          grid.H_z[idx] = dampFactor * (grid.H_z[idx] + dtOverMu * (dEx_dy - dEy_dx));
        }
      }
    }

    // --- Update E_x (electric field x-component) ---
    // E_x^(n+1) = E_x^n + (dt/ε₀) * ∂H_z/∂y
    for (let y = 1; y < res; y++) {
      for (let x = 0; x < res; x++) {
        const sigma = grid.getPMLDamping(x, y);
        const dampFactor = 1 / (1 + sigma * dt);

        for (let w = 0; w < nw; w++) {
          const idx = grid.index(x, y, w);
          const idx_ym1 = grid.index(x, y - 1, w);

          const dHz_dy = (grid.H_z[idx] - grid.H_z[idx_ym1]) * invDx;

          grid.E_x[idx] = dampFactor * (grid.E_x[idx] + dtOverEps * dHz_dy);
        }
      }
    }

    // --- Update E_y (electric field y-component) ---
    // E_y^(n+1) = E_y^n - (dt/ε₀) * ∂H_z/∂x
    for (let y = 0; y < res; y++) {
      for (let x = 1; x < res; x++) {
        const sigma = grid.getPMLDamping(x, y);
        const dampFactor = 1 / (1 + sigma * dt);

        for (let w = 0; w < nw; w++) {
          const idx = grid.index(x, y, w);
          const idx_xm1 = grid.index(x - 1, y, w);

          const dHz_dx = (grid.H_z[idx] - grid.H_z[idx_xm1]) * invDx;

          grid.E_y[idx] = dampFactor * (grid.E_y[idx] - dtOverEps * dHz_dx);
        }
      }
    }
  }

  /**
   * Inject a point source into the field grid.
   * Adds a sinusoidal E-field at the given grid position for a specific wavelength.
   *
   * @param grid The field grid
   * @param gx Grid x coordinate
   * @param gy Grid y coordinate
   * @param wavelengthIndex Which wavelength channel
   * @param amplitude Source amplitude
   * @param phase Current phase (radians)
   */
  injectPointSource(
    grid: FieldGrid,
    gx: number, gy: number,
    wavelengthIndex: number,
    amplitude: number,
    phase: number,
  ): void {
    if (gx < 0 || gx >= grid.resolution || gy < 0 || gy >= grid.resolution) return;
    if (wavelengthIndex < 0 || wavelengthIndex >= grid.wavelengthCount) return;

    const idx = grid.index(gx, gy, wavelengthIndex);
    // Soft source: add to existing field (not overwrite)
    grid.E_x[idx] += amplitude * Math.sin(phase);
  }

  /**
   * Inject a Gaussian beam source along a line in the grid.
   * Used for ray-to-field injection.
   */
  injectBeamSource(
    grid: FieldGrid,
    startGx: number, startGy: number,
    dirX: number, dirY: number,
    wavelengthIndex: number,
    amplitude: number,
    phase: number,
    beamWidth: number,
  ): void {
    const perpX = -dirY;
    const perpY = dirX;

    // Inject along a line perpendicular to beam direction
    const halfWidth = Math.ceil(beamWidth / 2);
    for (let i = -halfWidth; i <= halfWidth; i++) {
      const gx = Math.round(startGx + perpX * i);
      const gy = Math.round(startGy + perpY * i);

      if (gx < 0 || gx >= grid.resolution || gy < 0 || gy >= grid.resolution) continue;

      // Gaussian profile
      const gaussianFactor = Math.exp(-(i * i) / (2 * (beamWidth / 3) * (beamWidth / 3)));

      const idx = grid.index(gx, gy, wavelengthIndex);
      grid.E_x[idx] += amplitude * gaussianFactor * Math.sin(phase);
      grid.E_y[idx] += amplitude * gaussianFactor * Math.cos(phase);
    }
  }
}
